/**
 * CreditsDashboardPage — Credit management dashboard
 *
 * Features:
 *   - Table of all organizations with their credit balance
 *   - Click into an org to see detailed credit management:
 *     - Balance card with available credits
 *     - Grant credits (top up)
 *     - Burn credits (manual deduction)
 *     - Revoke credits (reverse a grant)
 *     - Adjust balance (dev mode only — set exact balance)
 *     - Paginated transaction log (all credit/debit events)
 *
 * @module packages/billing-admin/src/pages/CreditsDashboardPage
 */
import React, { useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import SlidePanel from '../components/SlidePanel';
import TopupModal from '../components/TopupModal';
import { PrecisionToggle, usePrecisionMode } from '../components/PrecisionToggle';
import { formatCents, formatCentsSigned } from '../utils/formatCents';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CreditOverviewRow {
  customer_id: string;
  customer_name: string;
  customer_email: string;
  balance_cents: number;
  currency: string;
}

interface CreditTransaction {
  id: string;
  amountCents: number;
  direction: string;
  reason: string;
  referenceId: string | null;
  referenceType: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

interface TransactionListResponse {
  transactions: CreditTransaction[];
  total: number;
  hasMore: boolean;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function useApiBase(): { baseUrl: string; token: string } {
  const { token, apiBaseUrl } = useAdminAuth();
  return { baseUrl: apiBaseUrl || window.location.origin, token: token ?? '' };
}

function useCreditsOverview() {
  const { baseUrl, token } = useApiBase();
  return useQuery<CreditOverviewRow[]>({
    queryKey: ['credits', 'overview'],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}/admin/credits/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      const json = await res.json();
      return json.data;
    },
    enabled: !!token,
  });
}

function useCreditBalance(customerId: string | null) {
  const { baseUrl, token } = useApiBase();
  return useQuery<{ balanceCents: number; currency: string } | null>({
    queryKey: ['credits', 'balance', customerId],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}/credits/balance?customerId=${customerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      const data = await res.json();
      return { balanceCents: data.balanceCents ?? data.balance_cents ?? 0, currency: data.currency ?? 'usd' };
    },
    enabled: !!customerId && !!token,
    refetchInterval: 5000,
  });
}

function useTransactions(customerId: string | null, page: number, pageSize: number) {
  const { baseUrl, token } = useApiBase();
  const offset = page * pageSize;
  return useQuery<TransactionListResponse>({
    queryKey: ['credits', 'transactions', customerId, page],
    queryFn: async () => {
      const res = await fetch(
        `${baseUrl}/credits/transactions?customerId=${customerId}&limit=${pageSize}&offset=${offset}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) return { transactions: [], total: 0, hasMore: false };
      return res.json();
    },
    enabled: !!customerId && !!token,
  });
}

function useGrantCredits() {
  const { baseUrl, token } = useApiBase();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { customerId: string; amountCents: number; reason: string; creditType?: string }) => {
      const res = await fetch(`${baseUrl}/admin/credits/grant`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...params, creditType: params.creditType ?? 'prepaid' }),
      });
      if (!res.ok) throw new Error(`Grant failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['credits'] }),
  });
}

function useBurnCredits() {
  const { baseUrl, token } = useApiBase();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { customerId: string; amountCents: number; reason: string; idempotencyKey?: string }) => {
      const res = await fetch(`${baseUrl}/credits/burn`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? `Burn failed: ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['credits'] }),
  });
}

function useRevokeCredits() {
  const { baseUrl, token } = useApiBase();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { customerId: string; amountCents: number; reason: string }) => {
      const res = await fetch(`${baseUrl}/credits/revoke`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      if (!res.ok) throw new Error(`Revoke failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['credits'] }),
  });
}

function usePlatformSettings() {
  const { baseUrl, token } = useApiBase();
  return useQuery<Record<string, string>>({
    queryKey: ['platform-settings'],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}/admin/platform-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return {};
      const data = await res.json();
      const settings: Record<string, string> = {};
      for (const item of data.data ?? []) {
        settings[item.key] = item.value;
      }
      return settings;
    },
  });
}

function useDeleteTransaction() {
  const { baseUrl, token } = useApiBase();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (transactionId: string) => {
      const res = await fetch(`${baseUrl}/admin/credits/transactions/${transactionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message ?? `Delete failed: ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['credits'] }),
  });
}

// ---------------------------------------------------------------------------
// Grouped transactions query
// ---------------------------------------------------------------------------

interface TransactionGroup {
  work_item_id: string;
  work_item_type: string;
  work_item_name: string;
  event_type: string;
  total_amount_cents: number;
  event_count: number;
  first_event_at: string;
  last_event_at: string;
}

interface GroupedTransactionsResponse {
  groups: TransactionGroup[];
  total: number;
  hasMore: boolean;
}

function useGroupedTransactions(customerId: string | null, page: number) {
  const { baseUrl, token } = useApiBase();
  return useQuery<GroupedTransactionsResponse>({
    queryKey: ['credits', 'transactions', 'grouped', customerId, page],
    queryFn: async () => {
      if (!customerId) return { groups: [], total: 0, hasMore: false };
      const res = await fetch(
        `${baseUrl}/credits/transactions/grouped?customerId=${customerId}&limit=20&offset=${page * 20}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) return { groups: [], total: 0, hasMore: false };
      return res.json();
    },
    enabled: !!customerId,
    refetchInterval: 15_000,
  });
}

function useGroupTransactions(customerId: string | null, workItemId: string | null) {
  const { baseUrl, token } = useApiBase();
  return useQuery<{ transactions: CreditTransaction[] }>({
    queryKey: ['credits', 'transactions', 'group', customerId, workItemId],
    queryFn: async () => {
      if (!customerId || !workItemId) return { transactions: [] };
      const res = await fetch(
        `${baseUrl}/credits/transactions/group/${encodeURIComponent(workItemId)}?customerId=${customerId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) return { transactions: [] };
      return res.json();
    },
    enabled: !!customerId && !!workItemId,
  });
}

// ---------------------------------------------------------------------------
// Action Modal
// ---------------------------------------------------------------------------

type ActionType = 'grant' | 'burn' | 'revoke';

function ActionModal({
  type, customerId, currentBalance, onClose,
}: {
  type: ActionType;
  customerId: string;
  currentBalance: number;
  onClose: () => void;
}): React.JSX.Element {
  const [amount, setAmount] = useState(100);
  const [reason, setReason] = useState('');

  const grantMutation = useGrantCredits();
  const burnMutation = useBurnCredits();
  const revokeMutation = useRevokeCredits();

  const titles: Record<ActionType, string> = {
    grant: 'Grant Credits (Top Up)',
    burn: 'Burn Credits (Deduct)',
    revoke: 'Revoke Credits (Reverse Grant)',
  };

  const descriptions: Record<ActionType, string> = {
    grant: 'Add credits to this customer\'s balance. Use for manual top-ups, promotional credits, or corrections.',
    burn: 'Deduct credits from this customer\'s balance. Use for manual burn-down or testing.',
    revoke: 'Reverse a previous credit grant. Creates a debit transaction to claw back credits.',
  };

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    executeAction();
  }

  function executeAction(): void {
    const amountCents = Math.round(amount * 100);
    const finalReason = reason || titles[type];

    if (type === 'grant') {
      grantMutation.mutate({ customerId, amountCents, reason: finalReason, creditType: 'prepaid' }, { onSuccess: () => onClose() });
    } else if (type === 'burn') {
      burnMutation.mutate({ customerId, amountCents, reason: finalReason, idempotencyKey: `manual-burn-${Date.now()}` }, { onSuccess: () => onClose() });
    } else if (type === 'revoke') {
      revokeMutation.mutate({ customerId, amountCents, reason: finalReason }, { onSuccess: () => onClose() });
    }
  }

  const activeMutation = type === 'grant' ? grantMutation : type === 'burn' ? burnMutation : revokeMutation;

  return (
    <SlidePanel open onClose={onClose} title={titles[type]} subtitle={descriptions[type]} size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Current balance */}
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-xs text-slate-500">Current Balance</p>
          <p className="text-lg font-semibold text-slate-900">{formatCents(currentBalance)}</p>
        </div>

        {/* Amount */}
        <div>
          <label className="text-sm font-medium text-slate-700">
            Amount (USD)
          </label>
          <input
            type="number"
            min={0.01}
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            className="mt-1 w-full rounded-lg border border-slate-300 px-4 py-2 text-lg"
          />
          <p className="mt-1 text-xs text-slate-400">
            {type === 'grant' ? `Balance after: ${formatCents(currentBalance + amount * 100)}`
              : `Balance after: ${formatCents(Math.max(0, currentBalance - amount * 100))}`}
          </p>
        </div>

        {/* Reason */}
        <div>
          <label className="text-sm font-medium text-slate-700">Reason</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={titles[type]}
            className="mt-1 w-full rounded-lg border border-slate-300 px-4 py-2"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={activeMutation.isPending}
            className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50 ${
              type === 'grant' ? 'bg-green-600 hover:bg-green-700'
              : type === 'burn' ? 'bg-red-600 hover:bg-red-700'
              : 'bg-orange-600 hover:bg-orange-700'
            }`}
          >
            {activeMutation.isPending ? 'Processing...' : `${titles[type].split(' ')[0]} $${amount}`}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
        </div>

        {activeMutation.isError && (
          <p className="text-sm text-red-600">{(activeMutation.error as Error).message}</p>
        )}
      </form>
    </SlidePanel>
  );
}

// ---------------------------------------------------------------------------
// Transaction Table
// ---------------------------------------------------------------------------

function TransactionTable({ customerId, allowDeletion }: { customerId: string; allowDeletion: boolean }): React.JSX.Element {
  const [page, setPage] = useState(0);
  const [viewMode, setViewMode] = useState<'grouped' | 'flat'>('grouped');
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [detailTx, setDetailTx] = useState<CreditTransaction | null>(null);
  const pageSize = 15;

  const transactionsQuery = useTransactions(customerId, page, pageSize);
  const groupedQuery = useGroupedTransactions(customerId, page);
  const groupDetailQuery = useGroupTransactions(customerId, expandedGroup);
  const deleteMutation = useDeleteTransaction();
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; direction: string; amountCents: number } | null>(null);

  const precisionMode = usePrecisionMode();

  function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleString();
  }

  function formatMetadata(meta: unknown): string {
    if (!meta || typeof meta !== 'object') return '—';
    const obj = meta as Record<string, unknown>;
    const parts: string[] = [];
    if (obj.model) parts.push(`model: ${obj.model}`);
    if (obj.input_tokens) parts.push(`in: ${obj.input_tokens}`);
    if (obj.output_tokens) parts.push(`out: ${obj.output_tokens}`);
    if (obj.work_item_name) parts.push(`${obj.work_item_type}: ${obj.work_item_name}`);
    return parts.length > 0 ? parts.join(', ') : JSON.stringify(obj).slice(0, 80);
  }

  const typeIcon = (type: string): string => {
    if (type === 'atp') return '⚡';
    if (type === 'uow') return '📋';
    if (type === 'project') return '📁';
    if (type === 'credit') return '💰';
    return '•';
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-semibold text-slate-700">Credit Transaction History</p>
        <div className="flex items-center gap-3">
          {/* View mode toggle */}
          <div className="flex rounded-lg border border-slate-200 p-0.5">
            <button
              onClick={() => setViewMode('grouped')}
              className={`rounded-md px-3 py-1 text-xs font-medium ${viewMode === 'grouped' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
            >
              Grouped
            </button>
            <button
              onClick={() => setViewMode('flat')}
              className={`rounded-md px-3 py-1 text-xs font-medium ${viewMode === 'flat' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
            >
              Flat
            </button>
          </div>
          {allowDeletion && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
              ⚠ Deletion Enabled
            </span>
          )}
        </div>
      </div>

      {/* ── Grouped View ── */}
      {viewMode === 'grouped' && (
        <>
          {groupedQuery.isLoading && <p className="text-sm text-slate-400">Loading transactions...</p>}
          {groupedQuery.data && groupedQuery.data.groups.length === 0 && (
            <p className="py-8 text-center text-sm text-slate-400">No transactions yet</p>
          )}
          {groupedQuery.data && groupedQuery.data.groups.length > 0 && (
            <>
              {/* Merge subgroups by work_item_id for display */}
              {(() => {
                const merged = new Map<string, {
                  work_item_id: string;
                  work_item_type: string;
                  work_item_name: string;
                  subgroups: TransactionGroup[];
                  total_amount_cents: number;
                  event_count: number;
                  last_event_at: string;
                }>();
                for (const g of groupedQuery.data.groups) {
                  const existing = merged.get(g.work_item_id);
                  if (existing) {
                    existing.subgroups.push(g);
                    existing.total_amount_cents += g.total_amount_cents;
                    existing.event_count += g.event_count;
                    if (g.last_event_at > existing.last_event_at) existing.last_event_at = g.last_event_at;
                  } else {
                    merged.set(g.work_item_id, {
                      work_item_id: g.work_item_id,
                      work_item_type: g.work_item_type,
                      work_item_name: g.work_item_name,
                      subgroups: [g],
                      total_amount_cents: g.total_amount_cents,
                      event_count: g.event_count,
                      last_event_at: g.last_event_at,
                    });
                  }
                }
                const mergedGroups = Array.from(merged.values()).sort((a, b) => b.last_event_at.localeCompare(a.last_event_at));

                return (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        <th className="pb-3 pr-4">Last Event</th>
                        <th className="pb-3 pr-4">Type</th>
                        <th className="pb-3 pr-4">Name</th>
                        <th className="pb-3 pr-4 text-right">Events</th>
                        <th className="pb-3 pr-4 text-right">Total</th>
                        <th className="pb-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {mergedGroups.map((g) => {
                        const typeLabel = g.work_item_type === 'atp' ? 'Atomic Task' :
                          g.work_item_type === 'uow' ? 'Unit of Work' :
                          g.work_item_type === 'project' ? 'Project' :
                          g.work_item_type === 'credit' ? 'Credit Top-Up' :
                          g.work_item_type === 'ai_usage' ? 'AI Usage' :
                          g.work_item_type || 'Usage';
                        const isCredit = g.work_item_type === 'credit';
                        return (
                        <React.Fragment key={g.work_item_id}>
                          <tr
                            className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                            onClick={() => setExpandedGroup(expandedGroup === g.work_item_id ? null : g.work_item_id)}
                          >
                            <td className="py-3 pr-4 text-xs text-slate-500">{formatDate(g.last_event_at)}</td>
                            <td className="py-3 pr-4">
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${isCredit ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                                {typeLabel}
                              </span>
                            </td>
                            <td className="py-3 pr-4">
                              <p className="font-medium text-slate-800">{g.work_item_name}</p>
                              {g.work_item_id !== 'ungrouped' && !g.work_item_id.startsWith('credit_') && g.work_item_id !== 'ai_usage' && (
                                <p className="text-[10px] text-slate-400 font-mono">{g.work_item_id.slice(0, 8)}</p>
                              )}
                            </td>
                            <td className="py-3 pr-4 text-right tabular-nums text-slate-600">{g.event_count}</td>
                            <td className={`py-3 pr-4 text-right font-semibold tabular-nums ${isCredit ? 'text-emerald-700' : 'text-red-700'}`}>
                              {isCredit ? '+' : '-'}{formatCents(g.total_amount_cents, precisionMode)}
                            </td>
                            <td className="py-3 text-xs text-slate-400">
                              {expandedGroup === g.work_item_id ? '▲' : '▼'}
                            </td>
                          </tr>
                          {/* Expanded: show subgroups by event type */}
                          {expandedGroup === g.work_item_id && (
                            <tr className="bg-slate-50">
                              <td colSpan={6} className="px-6 py-3">
                                {g.subgroups.length > 1 || (g.subgroups.length === 1 && g.subgroups[0]!.event_type !== 'credit_topup') ? (
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="border-b border-slate-200 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                        <th className="pb-2 pr-3">Event Type</th>
                                        <th className="pb-2 pr-3 text-right">Events</th>
                                        <th className="pb-2 pr-3 text-right">Amount</th>
                                        <th className="pb-2 pr-3">Last Event</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {g.subgroups.sort((a, b) => b.last_event_at.localeCompare(a.last_event_at)).map((sg) => (
                                        <tr key={sg.event_type} className="border-b border-slate-100">
                                          <td className="py-2 pr-3">
                                            <span className="inline-flex items-center rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-600">
                                              {sg.event_type}
                                            </span>
                                          </td>
                                          <td className="py-2 pr-3 text-right tabular-nums text-slate-500">{sg.event_count}</td>
                                          <td className="py-2 pr-3 text-right font-medium tabular-nums text-red-600">
                                            {formatCents(sg.total_amount_cents, precisionMode)}
                                          </td>
                                          <td className="py-2 pr-3 text-slate-400">{formatDate(sg.last_event_at)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                ) : (
                                  <p className="text-xs text-slate-400">Single event type — no subgroups</p>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                );
              })()}
              <div className="mt-4 flex items-center justify-between">
                <p className="text-xs text-slate-400">
                  {groupedQuery.data.total} groups · Page {page + 1}
                </p>
                <div className="flex gap-2">
                  <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-30">← Prev</button>
                  <button onClick={() => setPage(page + 1)} disabled={!groupedQuery.data.hasMore}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-30">Next →</button>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ── Flat View ── */}
      {viewMode === 'flat' && (
        <>
          {transactionsQuery.isLoading && <p className="text-sm text-slate-400">Loading transactions...</p>}
          {transactionsQuery.data && transactionsQuery.data.transactions.length === 0 && (
            <p className="py-8 text-center text-sm text-slate-400">No transactions yet</p>
          )}
          {transactionsQuery.data && transactionsQuery.data.transactions.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      <th className="pb-3 pr-4">Date</th>
                      <th className="pb-3 pr-4">Type</th>
                      <th className="pb-3 pr-4">Reason</th>
                      <th className="pb-3 pr-4 text-right">Amount</th>
                      <th className="pb-3 pr-4">Ref</th>
                      {allowDeletion && <th className="pb-3"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {transactionsQuery.data.transactions.map((tx) => (
                      <tr key={tx.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                        onClick={() => setDetailTx(tx)}>
                        <td className="py-2.5 pr-4 text-xs text-slate-500">{formatDate(tx.createdAt)}</td>
                        <td className="py-2.5 pr-4">
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                            tx.direction === 'credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {tx.direction === 'credit' ? '↑ Credit' : '↓ Debit'}
                          </span>
                        </td>
                        <td className="py-2.5 pr-4 text-slate-700">{tx.reason}</td>
                        <td className={`py-2.5 pr-4 text-right font-medium tabular-nums ${
                          tx.direction === 'credit' ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {formatCentsSigned(tx.amountCents, tx.direction, precisionMode)}
                        </td>
                        <td className="py-2.5 pr-4 text-xs text-slate-400">
                          {tx.referenceType ? tx.referenceType : '—'}
                        </td>
                        {allowDeletion && (
                          <td className="py-2.5 pl-2" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => setDeleteTarget({ id: tx.id, direction: tx.direction, amountCents: tx.amountCents })}
                              className="rounded-md bg-red-50 px-2 py-1 text-[10px] font-medium text-red-600 opacity-50 hover:opacity-100"
                              title="Delete transaction"
                            >✕</button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <p className="text-xs text-slate-400">
                  Page {page + 1} of {Math.ceil(transactionsQuery.data.total / pageSize) || 1}
                </p>
                <div className="flex gap-2">
                  <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-30">← Prev</button>
                  <button onClick={() => setPage(page + 1)} disabled={!transactionsQuery.data.hasMore}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-30">Next →</button>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ── Slide-in detail panel ── */}
      {detailTx && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setDetailTx(null)}>
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative w-full max-w-md bg-white shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
              <h3 className="text-lg font-semibold text-slate-900">Transaction Details</h3>
              <button onClick={() => setDetailTx(null)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Amount</p>
                <p className={`text-2xl font-bold ${detailTx.direction === 'credit' ? 'text-green-700' : 'text-red-700'}`}>
                  {formatCentsSigned(detailTx.amountCents, detailTx.direction, precisionMode)}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Date</p>
                <p className="text-sm text-slate-700">{formatDate(detailTx.createdAt)}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Reason</p>
                <p className="text-sm text-slate-700">{detailTx.reason}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Direction</p>
                <p className="text-sm text-slate-700">{detailTx.direction}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Reference</p>
                <p className="text-sm text-slate-700">{detailTx.referenceType ?? '—'}</p>
                {detailTx.referenceId && <p className="text-xs font-mono text-slate-400">{detailTx.referenceId}</p>}
              </div>
              {detailTx.metadata && typeof detailTx.metadata === 'object' && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Metadata</p>
                  <div className="mt-1 rounded-lg bg-slate-50 p-3">
                    <pre className="text-xs text-slate-600 whitespace-pre-wrap break-all">
                      {String(JSON.stringify(detailTx.metadata, null, 2))}
                    </pre>
                  </div>
                </div>
              )}
              {allowDeletion && (
                <button
                  onClick={() => { setDeleteTarget({ id: detailTx.id, direction: detailTx.direction, amountCents: detailTx.amountCents }); setDetailTx(null); }}
                  className="w-full rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-100"
                >
                  Delete Transaction
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">Delete Transaction?</h3>
            <p className="mt-2 text-sm text-slate-600">
              This will permanently delete this {deleteTarget.direction} transaction
              ({deleteTarget.direction === 'credit' ? '+' : '-'}{formatCents(deleteTarget.amountCents, precisionMode)})
              and reverse its effect on the balance.
            </p>
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs text-amber-800">
                ⚠️ This action cannot be undone. The balance will be adjusted by{' '}
                <strong>{deleteTarget.direction === 'credit' ? '-' : '+'}{formatCents(deleteTarget.amountCents, precisionMode)}</strong>.
              </p>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setDeleteTarget(null)} className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
              <button
                onClick={() => {
                  deleteMutation.mutate(deleteTarget.id, {
                    onSuccess: () => setDeleteTarget(null),
                    onError: (e) => { alert(`Delete failed: ${e instanceof Error ? e.message : 'Unknown error'}`); },
                  });
                }}
                disabled={deleteMutation.isPending}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete & Reverse Balance'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function CreditsDashboardPage(): React.JSX.Element {
  const [selectedCustomer, setSelectedCustomer] = useState<CreditOverviewRow | null>(null);
  const [actionType, setActionType] = useState<ActionType | null>(null);
  const [topupOpen, setTopupOpen] = useState(false);
  const precisionMode = usePrecisionMode();

  const overviewQuery = useCreditsOverview();
  const balanceQuery = useCreditBalance(selectedCustomer?.customer_id ?? null);
  const platformSettings = usePlatformSettings();
  const { apiBaseUrl, token } = useAdminAuth();

  const allowDeletion = platformSettings.data?.['allow_transaction_deletion'] === 'true';
  const currentBalance = balanceQuery.data?.balanceCents ?? 0;

  return (
    <AdminLayout title="Credit Management">
      <div className="mx-auto max-w-6xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-950">Credit Management</h1>
            <p className="mt-1 text-sm text-slate-500">Manage credit balances, burn-down, and transaction history</p>
          </div>
          <PrecisionToggle />
        </div>

        {/* Organization table */}
        {!selectedCustomer && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-sm font-semibold text-slate-700 mb-4">Organizations</p>
            {overviewQuery.isLoading && <p className="text-sm text-slate-400">Loading...</p>}
            {overviewQuery.isError && <p className="text-sm text-red-600">Failed to load organizations</p>}
            {overviewQuery.data && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="pb-3 pr-4">Organization</th>
                    <th className="pb-3 pr-4">Email</th>
                    <th className="pb-3 pr-4 text-right">Balance</th>
                    <th className="pb-3 text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {overviewQuery.data.map((row) => {
                    const isLow = row.balance_cents < 1000;
                    return (
                      <tr
                        key={row.customer_id}
                        onClick={() => setSelectedCustomer(row)}
                        className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-purple-50/50"
                      >
                        <td className="py-3 pr-4">
                          <p className="font-medium text-slate-900">{row.customer_name}</p>
                          <p className="mt-0.5 font-mono text-[11px] text-slate-400">{row.customer_id}</p>
                        </td>
                        <td className="py-3 pr-4 text-slate-600">{row.customer_email || '—'}</td>
                        <td className="py-3 pr-4 text-right font-semibold tabular-nums text-slate-900">
                          {formatCents(row.balance_cents, precisionMode)}
                        </td>
                        <td className="py-3 text-right">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            isLow ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {isLow ? 'Low' : 'Healthy'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Detailed view for selected org */}
        {selectedCustomer && (
          <>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSelectedCustomer(null)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
              >
                ← Back to all
              </button>
              <h2 className="text-lg font-semibold text-slate-900">{selectedCustomer.customer_name}</h2>
              <span className="text-sm text-slate-400">{selectedCustomer.customer_email}</span>
            </div>

            {/* Balance card + action buttons */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Available Credits</p>
                <p className="mt-3 text-4xl font-bold tracking-tight text-slate-950">
                  {formatCents(currentBalance, precisionMode)}
                </p>
                <p className="mt-1 text-xs text-slate-400">{balanceQuery.data?.currency ?? 'usd'}</p>
              </div>

              <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">Credit Actions</p>
                <div className="grid grid-cols-4 gap-3 sm:grid-cols-4">
                  <button
                    onClick={() => setActionType('grant')}
                    className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-700 hover:bg-green-100"
                  >
                    ↑ Grant
                  </button>
                  <button
                    onClick={() => setActionType('burn')}
                    className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 hover:bg-red-100"
                  >
                    ↓ Burn
                  </button>
                  <button
                    onClick={() => setActionType('revoke')}
                    className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm font-medium text-orange-700 hover:bg-orange-100"
                  >
                    ↩ Revoke
                  </button>
                  <button
                    onClick={() => setTopupOpen(true)}
                    className="rounded-lg border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm font-medium text-cyan-700 hover:bg-cyan-100"
                  >
                    💳 Purchase
                  </button>
                </div>
              </div>
            </div>

            {/* Transaction history */}
            <TransactionTable customerId={selectedCustomer.customer_id} allowDeletion={allowDeletion} />
          </>
        )}

        {/* Action modal */}
        {actionType && selectedCustomer && (
          <ActionModal
            type={actionType}
            customerId={selectedCustomer.customer_id}
            currentBalance={currentBalance}
            onClose={() => setActionType(null)}
          />
        )}

        {/* Topup modal (Stripe purchase) */}
        {selectedCustomer && (
          <TopupModal
            open={topupOpen}
            onClose={() => setTopupOpen(false)}
            customerId={selectedCustomer.customer_id}
            customerName={selectedCustomer.customer_name}
            apiBaseUrl={apiBaseUrl || window.location.origin}
            token={token ?? ''}
            onSuccess={() => { setTopupOpen(false); }}
          />
        )}
      </div>
    </AdminLayout>
  );
}
